import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { type ImageType, useGetCollectionBenchmarkPickerQuery } from '@vizit/api';
import { useToast } from '@vizit/atoms';
import { BenchmarkNewDialog, type CollectionScoringControlsState, benchmarkCreatedToast } from '@vizit/collections';
import type { CreatedBenchmark } from '@vizit/collections';
import { BENCHMARK_MIN_IMAGES } from '@vizit/constants';
import { AddProductCategoriesDialog, BenchmarkPicker, CollectionScoringBar } from '@vizit/organisms';
import { ProductCategoryPicker, ScoringStatus } from '@vizit/organisms';
import * as React from 'react';
import { useWorkspaceSlug } from '../../../hooks/use-workspace-link';
import { usePage } from '../../providers';

const ADD_CATEGORIES_SUBTITLE =
  'Each category you add is a separate shopper audience. Switch between them to see how each one scores your assets.';

interface CollectionScoringControlsProps {
  /** The page's scoring state, shared with the grid */
  scoring: CollectionScoringControlsState;
  workspaceId?: string;
  collectionId: string;
  /** The collection's image type, locked at creation */
  imageType?: ImageType;
  /** How many assets the grid shows */
  assetCount: number;
  isScored?: boolean;
  /** Controls on the right of the bar, e.g. "Upload assets" */
  actions?: React.ReactNode;
  /** Additional CSS classes for the bar */
  className?: string;
}

/**
 * The collection page's Scoring Controls: which benchmark and product category the grid's scores are measured
 * against, with the dialogs to add categories and to create a benchmark.
 */
function CollectionScoringControls(props: CollectionScoringControlsProps) {
  const { scoring, workspaceId, collectionId, imageType, assetCount, actions, className } = props;
  const isScored = props.isScored ?? scoring.selectedScoring?.status === 'ready';
  const { onAuthenticationError } = usePage();
  const queryClient = useQueryClient();
  const router = useRouter();
  const workspaceSlug = useWorkspaceSlug();
  const { addToast } = useToast();
  const [isNewBenchmarkOpen, setIsNewBenchmarkOpen] = React.useState(false);

  // A new set starts empty, under its minimum, so it is not selected: the toast says what it needs and leads to it.
  const handleBenchmarkCreated = (benchmarkId: string, benchmark: CreatedBenchmark) => {
    setIsNewBenchmarkOpen(false);
    // The picker lists the new set on its next read, disabled until it has enough images.
    void queryClient.invalidateQueries({
      queryKey: useGetCollectionBenchmarkPickerQuery.getKey({
        workspace_id: workspaceId ?? '',
        collection_id: collectionId,
      }),
    });
    addToast(
      benchmarkCreatedToast(
        benchmark,
        { imageType: imageType ?? benchmark.scope, minimumImages: BENCHMARK_MIN_IMAGES },
        () =>
          router.navigate({
            to: '/w/$workspaceSlug/library/benchmarks/$benchmarkId',
            params: { workspaceSlug, benchmarkId },
          }),
      ),
    );
  };

  // "Add images" is a real link, so it opens in a new tab too; a plain click stays in the app.
  const underMinimumBenchmarkId = scoring.status.underMinimumBenchmark ? scoring.selectedBenchmark?.id : undefined;
  const underMinimumBenchmarkHref = underMinimumBenchmarkId
    ? `/w/${workspaceSlug}/library/benchmarks/${underMinimumBenchmarkId}`
    : null;
  const openUnderMinimumBenchmark = underMinimumBenchmarkId
    ? () =>
        router.navigate({
          to: '/w/$workspaceSlug/library/benchmarks/$benchmarkId',
          params: { workspaceSlug, benchmarkId: underMinimumBenchmarkId },
        })
    : null;

  return (
    <>
      <CollectionScoringBar
        assetCount={assetCount}
        // As on the benchmark page: "scored against" only once the selected pair's scores show.
        isScored={isScored}
        imageType={imageType}
        benchmarkControl={
          <BenchmarkPicker
            options={scoring.options}
            selectedKey={scoring.selectedBenchmarkKey}
            onSelectionChange={scoring.selectBenchmark}
            onCreateBenchmark={() => setIsNewBenchmarkOpen(true)}
            scope={imageType}
            isLoading={scoring.isOptionsLoading}
            isError={scoring.isOptionsError}
            onRetry={scoring.retryOptions}
          />
        }
        productCategoryControl={
          scoring.hasCategories ? (
            <ProductCategoryPicker
              scoredCategories={scoring.scoredCategories}
              processingCategories={scoring.processingCategories}
              failedCategories={scoring.failedCategories}
              pathsStatus={scoring.categoryPathsStatus}
              selectedCategoryId={scoring.selectedCategoryId}
              onSelectionChange={scoring.selectCategory}
              // A collection is never scored without a category, so one is always selected.
              allowNone={false}
              onAddCategory={scoring.canAddCategories ? scoring.openAddCategories : undefined}
            />
          ) : undefined
        }
        onAddProductCategory={scoring.openAddCategories}
        isAddProductCategoryDisabled={!scoring.canAddCategories}
        status={
          <ScoringStatus
            {...scoring.status}
            missingCategoryBenchmarkName={scoring.isAwaitingCategory ? scoring.selectedBenchmark?.name : null}
            onRetryCategories={scoring.isScoringsError ? scoring.retryScorings : null}
            addBenchmarkImagesHref={underMinimumBenchmarkHref}
            onAddBenchmarkImages={openUnderMinimumBenchmark}
          />
        }
        actions={actions}
        className={className}
      />

      <AddProductCategoriesDialog
        isOpen={scoring.isAddCategoriesOpen}
        onOpenChange={scoring.setIsAddCategoriesOpen}
        subtitle={ADD_CATEGORIES_SUBTITLE}
        categories={scoring.categories}
        totalCount={scoring.categoryTotalCount}
        onSearchChange={scoring.searchCategories}
        excludedCategoryIds={scoring.excludedCategoryIds}
        isLoading={scoring.isCategoriesLoading}
        isSearching={scoring.isSearchingCategories}
        isScorableOnly
        isLoadError={scoring.isCategoriesError}
        onRetry={scoring.retryCategories}
        errorMessage={scoring.addCategoriesError}
        onAdd={scoring.addCategories}
      />

      <BenchmarkNewDialog
        workspaceId={workspaceId}
        isOpen={isNewBenchmarkOpen}
        hideTrigger
        onOpenChange={setIsNewBenchmarkOpen}
        onSuccessfulCreate={handleBenchmarkCreated}
        onAuthenticationError={onAuthenticationError}
      />
    </>
  );
}

export { CollectionScoringControls };
export type { CollectionScoringControlsProps };
